import { useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { registerAuth } from '@/lib/api'

interface AuthTokenSyncProps {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * Registers Clerk's getToken with the API module AND gates children until
 * the token cache is primed.
 *
 * Previously this rendered nothing and lived as a sibling of <App />, so
 * App (and TanStack Query) started firing API requests on first paint —
 * before the asynchronous getToken() in registerAuth had resolved and the
 * synchronous authHeaders() cache had been populated. The first wave of
 * queries went out with no Authorization header and came back 401, and only
 * the second refetch (triggered by component remount on getToken change)
 * succeeded. On slow networks that 401 flicker was visible to the user.
 *
 * Wrapping App in an AuthTokenSync gate guarantees:
 *   - useAuth().isLoaded is true before any child renders
 *   - registerAuth's priming promise has resolved (success or failure)
 *   - the first API call from any child has a real token in the header
 */
export function AuthTokenSync({ children, fallback = null }: AuthTokenSyncProps) {
  const { getToken, isLoaded, isSignedIn } = useAuth()
  const [tokenPrimed, setTokenPrimed] = useState(false)

  useEffect(() => {
    if (!isLoaded) return

    let cancelled = false
    setTokenPrimed(false)

    // Always re-register so the API module has the freshest getToken closure
    // when isSignedIn flips (sign-in / sign-out / session refresh).
    void registerAuth(getToken).finally(() => {
      if (!cancelled) setTokenPrimed(true)
    })

    return () => {
      cancelled = true
    }
    // isSignedIn is intentionally in deps — a sign-in event must re-prime
    // the cache with the new session token before children re-render.
  }, [getToken, isLoaded, isSignedIn])

  if (!isLoaded || !tokenPrimed) {
    return <>{fallback}</>
  }

  return <>{children}</>
}
